import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import { PackageCard } from "@/components/PackageCard";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { PACKAGE_TYPES } from "@/lib/site";

const CATEGORIES = [
  { key: "", label: "All" },
  { key: "domestic", label: "Domestic" },
  { key: "international", label: "International" },
];
const DURATIONS = [
  { key: "", label: "Any duration" },
  { key: "short", label: "Up to 4 days" },
  { key: "mid", label: "5–7 days" },
  { key: "long", label: "8+ days" },
];
const BUDGETS = [
  { key: "", label: "Any budget" },
  { key: "20000", label: "Under ₹20,000" },
  { key: "50000", label: "Under ₹50,000" },
  { key: "100000", label: "Under ₹1,00,000" },
  { key: "200000", label: "Under ₹2,00,000" },
];

export default function Packages() {
  const [params, setParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const type = params.get("type") || "";
  const duration = params.get("duration") || "";
  const maxBudget = params.get("max_budget") || "";
  const search = params.get("search") || "";

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (type) q.type = type;
    if (duration) q.duration = duration;
    if (maxBudget) q.max_budget = maxBudget;
    if (search) q.search = search;
    api.get("/packages", { params: q })
      .then((r) => setPackages(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, type, duration, maxBudget, search]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const activeCount = [category, type, duration, maxBudget, search].filter(Boolean).length;

  return (
    <div className="pt-32 pb-24" data-testid="packages-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-sun mb-3">Packages</p>
          <h1 className="font-display text-5xl sm:text-6xl font-black leading-tight mb-4">
            Find your <span className="font-light italic">next story.</span>
          </h1>
          {search && (
            <p className="text-ink/60 mb-2" data-testid="search-results-label">
              Results for “{search}”
              <button onClick={() => update("search", "")} data-testid="clear-search-btn" className="ml-2 text-sun text-sm underline">clear</button>
            </p>
          )}
        </Reveal>

        <div className="mt-10 space-y-5" data-testid="filters-panel">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c.key} onClick={() => update("category", c.key)} data-testid={`filter-category-${c.label.toLowerCase()}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${category === c.key ? "bg-ink text-bone" : "bg-sand/60 text-ink/70 hover:bg-sand"}`}>
                {c.label}
              </button>
            ))}
            <span className="hidden sm:block w-px bg-border mx-2" />
            {PACKAGE_TYPES.map((t) => (
              <button key={t} onClick={() => update("type", type === t ? "" : t)} data-testid={`filter-type-${t.replace(/\s/g, "-")}`}
                className={`px-4 py-2.5 rounded-full text-sm capitalize transition-all duration-200 ${type === t ? "bg-sun text-bone font-semibold" : "border border-border text-ink/60 hover:border-sun hover:text-sun"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SlidersHorizontal size={16} className="text-ink/40" />
            <select value={duration} onChange={(e) => update("duration", e.target.value)} data-testid="filter-duration-select"
              className="bg-bone border border-border rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-sun cursor-pointer">
              {DURATIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <select value={maxBudget} onChange={(e) => update("max_budget", e.target.value)} data-testid="filter-budget-select"
              className="bg-bone border border-border rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-sun cursor-pointer">
              {BUDGETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
            {activeCount > 0 && (
              <button onClick={() => setParams({})} data-testid="clear-filters-btn"
                className="inline-flex items-center gap-1.5 text-sm text-sun font-medium hover:underline">
                <X size={14} /> Clear all ({activeCount})
              </button>
            )}
          </div>
        </div>

        <div className="mt-14">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse"><div className="aspect-[4/3] bg-sand rounded-2xl" /><div className="h-4 bg-sand rounded mt-3 w-2/3" /></div>
              ))}
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-20" data-testid="no-packages-message">
              <p className="font-display text-2xl font-bold mb-2">No packages match those filters</p>
              <p className="text-ink/60 mb-6">Try widening your search — or let us build a custom trip for you.</p>
              <button onClick={() => setParams({})} data-testid="reset-filters-btn" className="bg-ink text-bone px-7 py-3 rounded-full text-sm font-semibold hover:bg-ocean transition-colors">Show everything</button>
            </div>
          ) : (
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="packages-grid">
              {packages.map((pkg) => <PackageCard key={pkg.slug} pkg={pkg} />)}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
